import React from "react";
import { Link } from "react-router-dom";
import { trialCountdown } from "../trialCountdown";
import { BILLING_PLANS_PATH } from "../../../routes/paths";
import type { AccessLapse } from "../accessLapse";
import type { Trial } from "../types/subscriptions.types";
import lapsedRailArtwork from "../../../assets/svg/Dsahboard/desktop_sidebar_subscription_end_banner.png";
import lapsedPhoneArtwork from "../../../assets/svg/Dsahboard/mobile_sidebar_subscription_end_banner.webp";
import trialArtwork from "../../../assets/svg/sidebar/side-bar-banner-img.png";

export interface TrialBannerProps {
  /** The trial as `GET /billing/subscription/` reported it, or `null` while
   *  the read is still out — see the rail, which is the only caller. */
  trial: Trial | null | undefined;
  /**
   * What has run out, which is what decides the wording — the same value the
   * dashboard's strip is drawn from, and the same one `useSubscriptionGate`
   * words its locked buttons with. See `accessLapse`.
   *
   * It has to be told rather than read off `trial.active`, because a finished
   * trial stays on the record of a workspace that later bought a plan: only the
   * caller knows whether there is a purchase behind it. `null` while the
   * billing read is still out, so the card claims nothing about an account we
   * do not know yet.
   */
  ended?: AccessLapse;
}

/**
 * What has run out, at the foot of the rail: the last few days of a trial, and
 * the fact of either ending once the door is shut.
 *
 * Only the sharp end of the fortnight. Up to `TRIAL_URGENT_DAYS` the trial is a
 * fact somebody glances at rather than a deadline, and `TrialMeter`'s quiet bar
 * is the whole of what it owes them; this takes over at `countdown.urgent`, so
 * exactly one of the two is ever drawn. What changes is not the number — that
 * is the same countdown either way — but how loudly it is put and whether a way
 * onto a plan is put beside it.
 *
 * Two ways of saying it, on either side of the end date. The countdown while it
 * runs, and the plain fact of its end after: past the end date a countdown
 * would be describing something that is no longer true, and taking the card
 * away entirely would drop the one way onto a plan the rail carries, at the
 * moment it is most wanted. Same split the dashboard's strip and the locked
 * create buttons make.
 *
 * Three things it can say, and the caller decides which by handing over the
 * same `AccessLapse` the dashboard's strip is drawn from. The countdown while
 * the fortnight runs; "your free trial has ended" past its end date; and "your
 * subscription has ended" for a practice between prepaid terms, which until
 * now got no card at all. That silence was the safe half of the right answer —
 * naming the trial to a customer of twelve months' standing would be off by
 * about a year — but it also dropped the rail's only way back onto a plan at
 * the moment it is most wanted. The fix is the other words, not no card.
 *
 * Nothing at all otherwise: a workspace that never took a trial and has bought
 * nothing, one with days of its fortnight still to run, and every paying
 * workspace — which is the majority and must not be shown a countdown for a
 * trial it is not on, nor told anything of theirs has ended.
 *
 * Two sizes of it, for the reason `TrialMeter` gives: the rail is narrow and
 * this is crowded into the foot of it, while the phone's account menu hands it
 * the width of the screen among 16px rows. The base values are the phone's and
 * `md:` puts the rail's back.
 *
 * The artwork is a background rather than an `<img>`: it says nothing the words
 * over it do not, and as a background it crops to whatever width the rail has
 * at that breakpoint without the words moving with it. The fill behind it is
 * sampled from whichever image is in front, so the card reads the same for the
 * moment before it is decoded and if it never arrives at all.
 *
 * Which artwork, though, is decided by something else entirely: whether money
 * has ever changed hands. See `SKINS` on the dashboard's strip, which makes
 * the same split for the same reason — a practice between paid terms gets the
 * purple, and the trial keeps the blue it has always run in.
 *
 * The purple comes in two pieces, for the same reason there are two sizes: the
 * rail's slot is a tall-ish box 144px across and the phone's account menu
 * hands the card the width of the screen, which is nearly twice as wide as it
 * is high. One image cropped to both shapes loses its artwork in one of them,
 * so each is drawn for the box it fills with the swirl already in the corner
 * that box has free — top right for the rail, bottom right for the phone,
 * which is what the two background positions are following. The blue is one
 * image at both widths, as it always was.
 */
/**
 * The two looks the card has — see the dashboard's strip, which carries the
 * reasoning. Each colour is a whole class rather than a hex dropped into a
 * template, because Tailwind reads this file as text and a class built at
 * runtime never reaches the stylesheet.
 */
const SKINS = {
  /** A term that has run out. */
  lapsed: {
    phoneArt: lapsedPhoneArtwork,
    railArt: lapsedRailArtwork,
    fill: "#613D97",
    position: "bg-right-bottom md:bg-right-top",
    label: "text-[#613D97]",
    ring: "focus-visible:ring-offset-[#613D97]",
  },
  /** The fortnight, running or finished. */
  trial: {
    phoneArt: trialArtwork,
    railArt: trialArtwork,
    fill: "#3F5BF0",
    position: "bg-center",
    label: "text-[#3451D9]",
    ring: "focus-visible:ring-offset-[#3F5BF0]",
  },
} as const;

const TrialBanner: React.FC<TrialBannerProps> = ({ trial, ended = null }) => {
  const countdown = trialCountdown(trial);
  const closing = countdown?.urgent ? countdown : null;
  if (!closing && !ended) return null;

  // What the card is about, which is not always the trial. Two of the three
  // states are, and they keep the region's name so somebody tabbing the rail
  // hears the same landmark either side of the end date; a lapsed plan is a
  // different subject and says so.
  const aboutTrial = Boolean(closing) || ended === "trial";

  // And what it is about is also what it looks like: the purple belongs to a
  // paid term, the blue to the fortnight. Same rule as the dashboard's strip.
  const skin = aboutTrial ? SKINS.trial : SKINS.lapsed;

  return (
    <section
      aria-label={aboutTrial ? "Free trial" : "Subscription"}
      // Handed over as custom properties so the breakpoint stays in the class
      // list with every other one, rather than in a size question asked in
      // JavaScript. The blue sets both to the same file, which costs nothing —
      // one image declared twice is one request — and keeps the two skins the
      // same shape here.
      style={
        {
          "--lapse-art": `url(${skin.phoneArt})`,
          "--lapse-art-rail": `url(${skin.railArt})`,
          backgroundColor: skin.fill,
        } as React.CSSProperties
      }
      className={`mb-0 md:mb-5 3xl:mb-6 overflow-hidden rounded-card bg-[image:var(--lapse-art)] md:bg-[image:var(--lapse-art-rail)] bg-cover ${skin.position} px-4 py-4 md:px-3 md:py-3 3xl:px-3.5 3xl:py-3.5`}
    >
      {closing ? (
        <>
          {/* "Ends today" is a sentence and the day counts are a number with a
              noun after them, so the two are printed as they come rather than
              split into a figure and a unit — see `trialCountdown`. */}
          <p className="text-[19px] md:text-[15px] 3xl:text-[17px] font-semibold leading-tight text-white">
            {closing.label}
          </p>
          <p className="mt-1 md:mt-0.5 text-[13px] md:text-[10px] 3xl:text-[12px] leading-tight text-white/80">
            in your free trial
          </p>
        </>
      ) : (
        // One sentence rather than the countdown's heading and line under it:
        // there is no number left to make loud, and what is worth saying is the
        // whole of it. A size down from the countdown so it still sits on two
        // lines in the narrowest rail.
        //
        // The line breaks are placed by hand because the rail is too narrow to
        // be trusted to wrap either sentence where it reads — and they are only
        // taken from `md:` up, since the phone's account menu has the width of
        // the screen and the sentence as written fits it.
        <p className="text-[18px] md:text-[14px] 3xl:text-[16px] font-semibold leading-snug text-white">
          {ended === "plan" ? (
            <>
              Your <br className="hidden md:inline" /> subscription{" "}
              {/* The green the design lifts a word off the purple with — the
                  same one the dashboard's strip puts its first line in. It
                  falls on the word that carries the news. */}
              <span className="text-[#52FF89]">has ended</span>
            </>
          ) : (
            <>
              Your free <br className="hidden md:inline" /> trial has{" "}
              <span className="text-[#52FF89]">ended</span>
            </>
          )}
        </p>
      )}

      {/* A link rather than a button that navigates, for the reason the
          dashboard's strip gives: it is a screen of the app, so a middle click
          opens it in a tab and the status bar says where it leads.

          All three states lead to the same place; only the label changes. A
          fortnight still running is being made an offer, and "Upgrade to Pro"
          is the offer the trial's own screens make. Somebody already shut out
          is owed the way back in instead, whichever of the two ran out — and
          for a practice that has been buying terms for a year, "upgrade" would
          be the wrong word for what they are doing anyway.

          The price list rather than the tab's default view, for the reason the
          dashboard's strip gives: all three states are here to buy a term, and
          the tab left to itself opens on the one they already have. */}
      <Link
        to={BILLING_PLANS_PATH}
        // Solid white with the label in the colour it stands on — the one
        // thing on the card that is pressed, so it is the one thing filled in.
        className={`mt-5 md:mt-6 3xl:mt-7 block w-full rounded-card bg-white px-2 py-2.5 md:py-1.5 3xl:py-2 text-center text-[14px] md:text-[11px] 3xl:text-[13px] font-medium ${skin.label} transition-colors hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 ${skin.ring}`}
      >
        {ended === "trial" ? "Upgrade to Pro" : "Subscribe now"}
      </Link>
    </section>
  );
};

export default TrialBanner;
